// Booking events for the VENUE's own tags, when the widget is embedded in the
// venue's website (lib/embed-context.ts): pushed to the page's dataLayer so
// the venue's Google Tag Manager can fire whatever it runs (GA4, Google Ads,
// Meta, …) on them, with its own triggers.
//
//   vertu_begin_checkout  vertu: {tenant, value, currency}
//   vertu_generate_lead   vertu: {tenant, enquiry_id, value, currency}
//   vertu_purchase        vertu: {tenant, booking_reference, value, currency},
//                         ecommerce: {transaction_id, value, currency}
//
// Each event follows a {vertu: null} push (and the purchase an
// {ecommerce: null} push, Google's recommended clear): GTM merges every push
// into one data model, so without the clear a key of one event (a lead's
// enquiry_id) would still be there for the next (a purchase).
//
// Values are in major units (rands), like state/tracking.ts. Never personal
// data: no name, email or phone.
//
// NOT behind Vertu's marketing consent (tracking.ts canTrack), on purpose.
// That gate decides what VERTU sends to the ad platforms. These pushes reach
// no platform by themselves: they land on the venue's own page, where the
// venue's own GTM container and cookie banner decide what (if anything)
// fires — the venue is the controller of its page and answers for its tags.
// Gating them on Vertu's checkout box as well would hide every booking from a
// venue whose banner the guest already accepted. Nothing is pushed on the
// hosted booking page: there is no venue container there to listen.
//
// Not Vertu's ad tracking: what Vertu sends to Meta, GA4 and Google Ads, its
// consent gate and its dedup IDs stay in tracking.ts.

import { isEmbedded } from "../lib/embed-context";

export type DataLayerEntry = Record<string, unknown>;

/** Cents to major units; anything that is not a finite amount is unknown (null). */
export function majorUnits(cents: number | null | undefined): number | null {
  return typeof cents === "number" && Number.isFinite(cents) ? cents / 100 : null;
}

// Every key is always present, null when unknown, besides the clear before
// each event: a reader of one event never meets a value from another.
function amount(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const CLEAR_VERTU: DataLayerEntry = { vertu: null };
const CLEAR_ECOMMERCE: DataLayerEntry = { ecommerce: null };

export function beginCheckoutEntries(input: {
  tenant: string;
  value: number | null;
  currency: string;
}): DataLayerEntry[] {
  return [
    { ...CLEAR_VERTU },
    {
      event: "vertu_begin_checkout",
      vertu: { tenant: input.tenant, value: amount(input.value), currency: input.currency },
    },
  ];
}

export function leadEntries(input: {
  tenant: string;
  enquiryId: string | null | undefined;
  value: number | null | undefined;
  currency: string;
}): DataLayerEntry[] {
  return [
    { ...CLEAR_VERTU },
    {
      event: "vertu_generate_lead",
      vertu: {
        tenant: input.tenant,
        enquiry_id: input.enquiryId ?? null,
        value: amount(input.value),
        currency: input.currency,
      },
    },
  ];
}

export function purchaseEntries(input: {
  tenant: string;
  reference: string | null;
  value: number | null;
  currency: string;
}): DataLayerEntry[] {
  const value = amount(input.value);
  return [
    { ...CLEAR_VERTU },
    { ...CLEAR_ECOMMERCE },
    {
      event: "vertu_purchase",
      vertu: {
        tenant: input.tenant,
        booking_reference: input.reference,
        value,
        currency: input.currency,
      },
      ecommerce: { transaction_id: input.reference, value, currency: input.currency },
    },
  ];
}

// ─── Once per booking ──────────────────────────────────────────────────────
// A booking's purchase is pushed once (and tracking.ts fires its ad
// conversion once): remembered in memory (a view that renders twice) AND in
// sessionStorage (the confirmation shown again after a reload, or the return
// address opened again). sessionStorage, not localStorage: it lasts the
// visit, which is as long as the confirmation can be shown again.
type SessionStore = Pick<Storage, "getItem" | "setItem">;

export function sessionStore(): SessionStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Claim the one go at `key`: true the first time, false once it was claimed
 * in this page (`seen`) or this visit (`store`). Storage that throws
 * (blocked, private mode) still leaves the in-memory guard.
 */
export function claimOnce(key: string, seen: Set<string>, store: SessionStore | null): boolean {
  if (seen.has(key)) return false;
  seen.add(key);
  try {
    if (store?.getItem(key) === "1") return false;
    store?.setItem(key, "1");
  } catch {
    /* storage unavailable: the in-memory guard above is all there is */
  }
  return true;
}

const PURCHASE_KEY = "vt-dl-purchase";
const pushedPurchases = new Set<string>();

function push(entries: DataLayerEntry[]): void {
  try {
    const w = window as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    for (const entry of entries) w.dataLayer.push(entry);
  } catch {
    /* never throw into the booking flow */
  }
}

// ─── Pushes (embedded only) ────────────────────────────────────────────────
export function pushHostBeginCheckout(input: {
  tenant: string;
  value: number | null;
  currency: string;
}): void {
  if (!isEmbedded()) return;
  push(beginCheckoutEntries(input));
}

export function pushHostLead(input: {
  tenant: string;
  enquiryId: string | null | undefined;
  value: number | null | undefined;
  currency: string;
}): void {
  if (!isEmbedded()) return;
  push(leadEntries(input));
}

/**
 * A booking is confirmed: every path that shows a confirmation calls this
 * (the in-widget confirmation, the return from Paystack, a checkout that saw
 * its earlier payment go through, the spa deposit return), each with the
 * amount it has. Without a reference there is nothing to know the booking by
 * again, so it is pushed as it comes.
 */
export function pushHostPurchase(input: {
  tenant: string;
  reference: string | null;
  value: number | null;
  currency: string;
}): void {
  if (!isEmbedded()) return;
  if (
    input.reference &&
    !claimOnce(`${PURCHASE_KEY}:${input.tenant}:${input.reference}`, pushedPurchases, sessionStore())
  )
    return;
  push(purchaseEntries(input));
}
